import { Injectable, inject, signal, computed } from '@angular/core';
import { Firestore, doc, getFirestore, onSnapshot, setDoc, Unsubscribe } from 'firebase/firestore';
import { Functions, getFunctions } from 'firebase/functions';

import { environment } from '../../../environments/environment';
import { HOME_REGION, Region } from '../region';
import { OrgRegionResolver } from '../org-region-resolver';
import { decideUrlOrg, orgUrl, pickDefaultOrgId } from '../org-url';

import { Organization } from '../models/organization.model';
import { OrganizationMember } from '../models/member.model';
import { Role, DEFAULT_MEMBER_PERMISSIONS } from '../models/role.model';
import { AuthBridgeService } from './auth-bridge.service';
import { DataStoreService } from './data-store.service';
import { FirebaseService } from './firebase.service';
import { OrganizationService } from './organization.service';
import { OrganizationMemberService } from './organization-member.service';
import { RoleService } from './role.service';

@Injectable({
  providedIn: 'root',
})
export class OrgContextService {
  private readonly firebase = inject(FirebaseService);
  private readonly dataStore = inject(DataStoreService);
  private readonly organizationService = inject(OrganizationService);
  private readonly memberService = inject(OrganizationMemberService);
  private readonly roleService = inject(RoleService);
  private readonly authBridge = inject(AuthBridgeService);
  // Data residency: memoized (ca) region connections + the prod-safe fallback
  // for a failed directory read when no remote region is configured.
  private readonly regionResolver = new OrgRegionResolver({
    connect: (orgId) =>
      this.authBridge.bridge.connectRegion({
        orgId,
        regionalConfigs: this.firebase.regionalConfigs,
        firestore: this.firebase.globalFirestore,
      }),
    remoteConfigured: () => Object.keys(this.firebase.regionalConfigs).length > 0,
    homeConnection: () => ({
      region: HOME_REGION,
      app: this.firebase.globalApp,
      auth: this.firebase.auth,
      isRegional: false,
    }),
  });

  readonly currentOrg = computed(() => this.dataStore.currentOrganization());
  readonly currentMember = computed(() => this.dataStore.currentMember());
  readonly currentRole = computed(() => this.dataStore.currentRole());
  // flag: Only an organization owner or admin role can administer Folder Knowledge.
  readonly isCurrentUserAdmin = computed(() => {
    const organization = this.currentOrg();
    const member = this.currentMember();
    const role = this.currentRole();
    const uid = this.firebase.auth.currentUser?.uid;
    return !!organization && !!uid && (
      organization.ownerId === uid || member?.role === 'admin' || role?.id === 'admin'
    );
  });

  readonly userOrganizations = signal<Organization[]>([]);
  readonly loading = signal(false);
  /**
   * Data residency: orgs the user belongs to whose region can't be reached
   * right now (no config in this build, region not live, region-token
   * refused) → reason. They're absent from userOrganizations.
   */
  readonly unavailableOrgs = signal<Record<string, string>>({});

  private currentOrgUnsub: Unsubscribe | null = null;
  private currentMemberUnsub: Unsubscribe | null = null;
  private currentRoleUnsub: Unsubscribe | null = null;
  private subscribedOrgId: string | null = null;
  private subscribedRoleId: string | null = null;

  // Legacy cross-app selection (`users/{uid}.currentOrganizationId`), captured
  // in loadUserOrganizations. The org now lives in the URL (`/o/{orgId}`): this
  // is only a last-resort hint for legacy URLs without one, never the source of
  // truth when the URL names an org. Still WRITTEN on an explicit switch during
  // the transition (other apps read it) — see switchOrgAndReload.
  private remoteCurrentOrgId: string | null = null;

  /** Per-device last-used org hint (localStorage) for URLs without an org. */
  private get storageKey(): string {
    const uid = this.firebase.auth.currentUser?.uid;
    return `admin-console-current-org-${uid}`;
  }

  /**
   * Load the user's orgs and activate one: `urlOrgId` (the `/o/{orgId}` of the
   * URL being opened) when given — and ONLY that org: one the user doesn't
   * belong to activates nothing, so the URL guard can show "no access" instead
   * of silently landing in another org. Without a URL org, the default
   * (device hint → legacy currentOrganizationId → first org).
   */
  async init(urlOrgId: string | null = null) {
    await this.firebase.auth.authStateReady();
    const user = this.firebase.auth.currentUser;
    if (!user) return;

    await this.loadUserOrganizations();

    // If the user just completed a Microsoft sign-in, the centralized auth
    // portal stashed the Entra tid + oid in a short-lived `.crewdle.ai` cookie
    // so we can run org auto-discovery (every Crewdle org linked to that tenant
    // the user is assigned to gets joined).
    const pendingTid = this.readMsCookie('ms_tid');
    const pendingOid = this.readMsCookie('ms_oid');
    if (pendingTid) {
      this.clearMsCookie('ms_tid');
      this.clearMsCookie('ms_oid');
      try {
        await this.organizationService.syncMicrosoftRoleOnLogin(pendingTid, pendingOid ?? undefined);
        await this.loadUserOrganizations();
      } catch {
        // Non-blocking — fall through to whatever orgs we already have.
      }
    } else {
      // Routine role refresh for orgs the user already belongs to.
      this.organizationService.syncMicrosoftRoleOnLogin().catch(() => {});
    }

    if (urlOrgId) {
      await this.activateOrg(urlOrgId);
      return;
    }

    // No org in the URL: the per-device hint, then the legacy cross-app
    // `currentOrganizationId`, then the first org.
    const savedOrgId = this.readHint();
    const hinted = [savedOrgId, this.remoteCurrentOrgId];
    const target = pickDefaultOrgId({
      activeOrgId: null,
      hintOrgId: savedOrgId,
      remoteOrgId: this.remoteCurrentOrgId,
      memberOrgIds: this.userOrganizations().map((o) => o.id),
    });
    const hintedTarget = hinted.find((id): id is string => !!id && id === target);

    // Data residency: the hinted org lives in a region this build/stack can't
    // reach → surface "region unavailable" rather than silently landing the
    // user in another (ca) org.
    const unavailable = this.unavailableOrgs();
    const pinned = hinted.find((id): id is string => !!id && id in unavailable);
    if (!hintedTarget && pinned) {
      this.firebase.bindUnavailable(pinned, unavailable[pinned]);
      return;
    }

    if (target) {
      await this.switchOrg(target);
    }
  }

  /**
   * The org a URL without one resolves to (legacy URLs, `/`): the org active in
   * this tab, else the device hint, else the legacy currentOrganizationId, else
   * the first org. Null when the user has none.
   */
  defaultOrgId(): string | null {
    return pickDefaultOrgId({
      activeOrgId: this.currentOrg()?.id ?? null,
      hintOrgId: this.readHint(),
      remoteOrgId: this.remoteCurrentOrgId,
      memberOrgIds: this.userOrganizations().map((o) => o.id),
    });
  }

  /** `/o/{activeOrgId}{path}` — or `path` itself while no org is active. */
  orgUrl(path: string): string {
    const orgId = this.currentOrg()?.id;
    return orgId ? orgUrl(orgId, path) : path;
  }

  /**
   * Activate the org named by the URL. Never falls back to another org:
   * - 'ok': it is (now) the active org;
   * - 'unavailable': one of the user's orgs whose region can't be reached
   *   (FirebaseService.regionError is set → /region-unavailable);
   * - 'no-access': not one of the user's orgs — nothing is activated;
   * - 'reload': another org is already active in this tab — the caller does a
   *   full load into the URL so every service rehydrates.
   */
  async activateOrg(orgId: string): Promise<'ok' | 'unavailable' | 'no-access' | 'reload'> {
    const decision = decideUrlOrg({
      urlOrgId: orgId,
      activeOrgId: this.currentOrg()?.id ?? null,
      memberOrgIds: this.userOrganizations().map((o) => o.id),
      unavailableOrgIds: Object.keys(this.unavailableOrgs()),
    });
    switch (decision) {
      case 'active':
        return 'ok';
      case 'reload':
        return 'reload';
      case 'no-access':
        return 'no-access';
      case 'unavailable':
        this.stopCurrentOrgSync();
        this.firebase.bindUnavailable(orgId, this.unavailableOrgs()[orgId]);
        return 'unavailable';
      case 'activate':
        try {
          await this.switchOrg(orgId);
          return 'ok';
        } catch {
          return this.firebase.regionError() ? 'unavailable' : 'no-access';
        }
    }
  }

  private readHint(): string | null {
    try {
      return localStorage.getItem(this.storageKey);
    } catch {
      return null;
    }
  }

  /** Read a `crewdle_<name>` cookie set by the auth portal on `.crewdle.ai`. */
  private readMsCookie(name: string): string | null {
    const prefix = `crewdle_${name}=`;
    for (const c of document.cookie.split(';')) {
      const t = c.trim();
      if (t.startsWith(prefix)) return decodeURIComponent(t.substring(prefix.length));
    }
    return null;
  }

  /** Expire a `crewdle_<name>` cookie once consumed (must match set domain). */
  private clearMsCookie(name: string): void {
    document.cookie = `crewdle_${name}=;max-age=0;path=/;domain=.crewdle.ai`;
  }

  async loadUserOrganizations() {
    const { getDoc, doc } = await import('firebase/firestore');
    const user = this.firebase.auth.currentUser;
    if (!user) return;

    const userDoc = await getDoc(doc(this.firebase.globalFirestore, 'users', user.uid));
    const userData = userDoc.data();
    const orgIds: string[] = userData?.['organizationIds'] || [];
    // Cross-app shared selection (same field open-webui/connect use).
    this.remoteCurrentOrgId = (userData?.['currentOrganizationId'] as string) || null;

    // Load every org in parallel rather than serially — this is on the cold
    // bootstrap path, so N serial round trips here directly delays first paint.
    // Pass the orgIds we just read so each getOrganization skips its own
    // redundant user-doc access re-check. Orgs the user can no longer access
    // resolve to null and are dropped.
    //
    // Data residency: each org doc is read from ITS region's project
    // (connectRegion is de-duplicated per org and signs a region in once per
    // uid). An org whose region can't be reached is recorded, never read from ca.
    const unavailable: Record<string, string> = {};
    const orgs = (
      await Promise.all(
        orgIds.map(async (orgId) => {
          let db: Firestore;
          try {
            db = await this.firestoreForOrg(orgId);
          } catch (err) {
            unavailable[orgId] = err instanceof Error ? err.message : String(err);
            return null;
          }
          return this.organizationService.getOrganization(orgId, false, orgIds, db).catch(() => null);
        }),
      )
    ).filter((o): o is Organization => o !== null);

    this.unavailableOrgs.set(unavailable);
    this.userOrganizations.set(orgs);
  }

  async refreshCurrentOrg() {
    const orgId = this.currentOrg()?.id;
    if (!orgId) return;
    await this.organizationService.getOrganization(orgId, true);
  }

  /**
   * User-initiated org switch: opens the other org's URL (`/o/{orgId}{path}`)
   * as a full page load so every service / signal / Firestore subscription
   * rehydrates against the new org. The org is carried by the URL; the user
   * doc is no longer what decides it.
   */
  switchOrgAndReload(orgId: string, path = '/home'): void {
    const userOrgs = this.userOrganizations();
    // An org whose region is unreachable is still one of the user's orgs: the
    // reload lands on /region-unavailable for it instead of failing here.
    if (!userOrgs.some((o) => o.id === orgId) && !(orgId in this.unavailableOrgs())) {
      throw new Error('Unauthorized: user is not a member of this organization');
    }
    localStorage.setItem(this.storageKey, orgId);
    // Transition: keep the legacy cross-app `currentOrganizationId` in step with
    // an explicit switch — other Crewdle apps still read it. Admin itself never
    // reads it while the URL names an org.
    const uid = this.firebase.auth.currentUser?.uid;
    const persisted = uid
      ? setDoc(
          doc(this.firebase.globalFirestore, 'users', uid),
          { currentOrganizationId: orgId },
          { merge: true },
        ).catch(() => undefined)
      : Promise.resolve();
    persisted.finally(() => {
      window.location.assign(orgUrl(orgId, path));
    });
  }

  async switchOrg(orgId: string) {
    this.loading.set(true);
    try {
      // Verify user has access to this organization. switchOrg is always
      // called with an org already present in userOrganizations (init() and the
      // invite flows both load it first), so reuse that loaded doc instead of a
      // forced re-fetch — startCurrentOrgSync below opens a live subscription
      // that reconciles any drift within milliseconds.
      const userOrgs = this.userOrganizations();
      const organization = userOrgs.find((o) => o.id === orgId);
      const unreachable = this.unavailableOrgs()[orgId];
      if (!organization && unreachable) {
        this.stopCurrentOrgSync();
        this.firebase.bindUnavailable(orgId, unreachable);
        throw new Error(unreachable);
      }
      if (!organization) {
        console.error('[OrgContext] switchOrg: user does not have access to organization', orgId);
        throw new Error('Unauthorized: user is not a member of this organization');
      }

      // Data residency: bind the org's regional project BEFORE any org read or
      // listener, so every FirebaseService org handle targets the right region.
      await this.bindOrgRegion(orgId);

      // Ensure system roles (admin, member) exist in Firestore for this org.
      // Fire-and-forget: role resolution below falls back to correct
      // permissions if the docs aren't seeded yet, so this must not block paint.
      this.roleService.seedSystemRolesIfMissing(orgId).catch(() => {});

      let member: OrganizationMember | null = null;
      let role: Role | null = null;

      try {
        member = await this.memberService.getCurrentMember(orgId);
      } catch {
        // Member doc may not exist yet
      }

      if (member) {
        try {
          role = await this.memberService.getCurrentMemberRole(orgId);
        } catch {
          role = this.buildFallbackRoleFromMember(member, organization);
        }
      }

      // Fallback: if still no role but user is org owner, grant admin
      if (!role && organization.ownerId === this.firebase.auth.currentUser?.uid) {
        role = this.buildAdminRole();
      }

      this.dataStore.updateState({
        currentOrganization: organization,
        currentMember: member,
        currentRole: role,
      });

      // Per-device last-used hint (legacy URLs without an org resolve to it).
      // Activating an org from a URL does NOT rewrite the user doc: only an
      // explicit switch does (switchOrgAndReload), so opening a link in one
      // tab doesn't move the other apps/tabs.
      try {
        localStorage.setItem(this.storageKey, orgId);
      } catch {
        // Storage disabled — the hint is a convenience.
      }
      this.startCurrentOrgSync(orgId);
    } finally {
      this.loading.set(false);
    }
  }

  /**
   * Firestore + callables of ANOTHER org's regional project, for read-only
   * cross-org views (staff `/usage?orgId=`). Resolves only when the user can
   * read that org's directory entry (i.e. is a member); otherwise null — the
   * caller stays on the active org's region and must not assume the org lives
   * there.
   */
  async handlesForOrg(orgId: string): Promise<{ region: Region; firestore: Firestore; functions: Functions } | null> {
    try {
      const connection = await this.authBridge.bridge.connectRegion({
        orgId,
        regionalConfigs: this.firebase.regionalConfigs,
        firestore: this.firebase.globalFirestore,
      });
      const endpoints = environment.regions[connection.region];
      if (!endpoints) return null;
      return {
        region: connection.region,
        firestore: getFirestore(connection.app),
        functions: getFunctions(connection.app, endpoints.functionsRegion),
      };
    } catch {
      return null;
    }
  }

  /** Firestore of the org's regional project (the global one for ca orgs). */
  private async firestoreForOrg(orgId: string): Promise<Firestore> {
    const connection = await this.regionResolver.resolve(orgId);
    return getFirestore(connection.app);
  }

  /**
   * Connect the org's region and point FirebaseService's org handles at it.
   * Failure = "region unavailable" (FirebaseService.regionError) — never a
   * fallback to the ca project (except a failed directory read in a build with
   * no remote region configured, see OrgRegionResolver).
   */
  private async bindOrgRegion(orgId: string): Promise<void> {
    try {
      const connection = await this.regionResolver.resolve(orgId);
      this.firebase.bindOrg(orgId, connection.region, connection.app);
    } catch (err) {
      this.stopCurrentOrgSync();
      this.firebase.bindUnavailable(orgId, err instanceof Error ? err.message : String(err));
      throw err;
    }
  }

  /**
   * Keep the current organization doc in sync so remote mutations (e.g.
   * agent-driven rename or Microsoft sync toggle) reflect live in the UI.
   */
  private startCurrentOrgSync(orgId: string): void {
    if (this.subscribedOrgId === orgId) return;
    this.currentOrgUnsub?.();
    this.currentMemberUnsub?.();
    this.currentRoleUnsub?.();
    this.currentRoleUnsub = null;
    this.subscribedRoleId = null;
    this.subscribedOrgId = orgId;

    const orgRef = doc(this.firebase.firestore, 'organizations', orgId);
    this.currentOrgUnsub = onSnapshot(orgRef, (snap) => {
      if (!snap.exists()) return;
      const current = this.dataStore.getProperty('currentOrganization');
      if (!current || current.id !== orgId) return;
      const data = snap.data();
      const updated: Organization = {
        ...current,
        name: data['name'] ?? current.name,
        // An upload / removal (this tab or another) shows in the switcher without a reload.
        logoUrl: typeof data['logoUrl'] === 'string' && data['logoUrl'] ? data['logoUrl'] : null,
        tids: data['tids'] ?? current.tids,
        usageReportSegments: data['usageReportSegments'] ?? current.usageReportSegments,
        creditBalance: data['creditBalance'] ?? current.creditBalance,
        overageAllowed: data['overageAllowed'] === true,
        milestones: data['milestones'] ?? current.milestones,
        onboardingProgram: data['onboardingProgram'] ?? current.onboardingProgram,
        // Live partner-status flips (e.g. a become-partner approval) light up
        // white-label branding and partner nav without a reload.
        isPartner: data['isPartner'] ?? current.isPartner,
        partnerId: data['partnerId'] ?? current.partnerId,
      };
      this.dataStore.updateState({ currentOrganization: updated });
    });

    const uid = this.firebase.auth.currentUser?.uid;
    if (!uid) return;
    const memberRef = doc(this.firebase.firestore, 'organizations', orgId, 'members', uid);
    this.currentMemberUnsub = onSnapshot(memberRef, (snap) => {
      if (this.subscribedOrgId !== orgId) return;
      if (!snap.exists()) {
        this.dataStore.updateState({ currentMember: null });
        this.resubscribeRole(orgId, null);
        return;
      }
      const data = snap.data();
      const updatedMember: OrganizationMember = {
        userId: data['userId'],
        role: data['role'],
        roleId: data['roleId'],
        permissions: data['permissions'],
        joinedAt: data['joinedAt'],
      };
      this.dataStore.updateState({ currentMember: updatedMember });
      const roleId = updatedMember.roleId || updatedMember.role || null;
      this.resubscribeRole(orgId, roleId);
    });
  }

  private resubscribeRole(orgId: string, roleId: string | null): void {
    if (this.subscribedRoleId === roleId) return;
    this.currentRoleUnsub?.();
    this.currentRoleUnsub = null;
    this.subscribedRoleId = roleId;

    if (!roleId) {
      const member = this.dataStore.getProperty('currentMember');
      const organization = this.dataStore.getProperty('currentOrganization');
      const fallback = member && organization
        ? this.buildFallbackRoleFromMember(member, organization)
        : null;
      this.dataStore.updateState({ currentRole: fallback });
      return;
    }

    if (roleId === 'admin') {
      this.dataStore.updateState({ currentRole: this.buildAdminRole() });
      return;
    }

    const roleRef = doc(this.firebase.firestore, 'organizations', orgId, 'roles', roleId);
    this.currentRoleUnsub = onSnapshot(roleRef, (snap) => {
      if (this.subscribedOrgId !== orgId || this.subscribedRoleId !== roleId) return;
      if (!snap.exists()) {
        const member = this.dataStore.getProperty('currentMember');
        const organization = this.dataStore.getProperty('currentOrganization');
        const fallback = member && organization
          ? this.buildFallbackRoleFromMember(member, organization)
          : null;
        this.dataStore.updateState({ currentRole: fallback });
        return;
      }
      const data = snap.data();
      const role: Role = {
        id: snap.id,
        name: data['name'],
        description: data['description'] || '',
        permissions: data['permissions'] || [],
        isSystem: data['isSystem'] ?? true,
        createdAt: data['createdAt'],
        updatedAt: data['updatedAt'],
        createdBy: data['createdBy'] || '',
      };
      this.dataStore.updateState({ currentRole: role });
    });
  }

  stopCurrentOrgSync(): void {
    this.currentOrgUnsub?.();
    this.currentMemberUnsub?.();
    this.currentRoleUnsub?.();
    this.currentOrgUnsub = null;
    this.currentMemberUnsub = null;
    this.currentRoleUnsub = null;
    this.subscribedOrgId = null;
    this.subscribedRoleId = null;
  }

  hasPermission(permission: string) {
    return computed(() => {
      const role = this.currentRole();
      if (!role) {
        return this.isCurrentUserOwner();
      }
      return role.permissions.includes(permission) || role.permissions.includes('*');
    });
  }

  async checkPermission(permission: string): Promise<boolean> {
    const role = this.currentRole();
    if (!role) {
      return this.isCurrentUserOwner();
    }
    return role.permissions.includes(permission) || role.permissions.includes('*');
  }

  private isCurrentUserOwner(): boolean {
    const org = this.currentOrg();
    const uid = this.firebase.auth.currentUser?.uid;
    return !!org && !!uid && org.ownerId === uid;
  }

  private buildFallbackRoleFromMember(member: OrganizationMember, organization: Organization): Role {
    const roleId = member.roleId || member.role;
    const isOwner = organization.ownerId === this.firebase.auth.currentUser?.uid;
    const isAdmin = isOwner || roleId === 'admin' || member.role === 'admin';
    return {
      id: roleId || 'member',
      name: (roleId || 'member').charAt(0).toUpperCase() + (roleId || 'member').slice(1),
      description: '',
      permissions: isAdmin ? ['*'] : DEFAULT_MEMBER_PERMISSIONS,
      isSystem: true,
      createdAt: null,
      updatedAt: null,
      createdBy: '',
    };
  }

  private buildAdminRole(): Role {
    return {
      id: 'admin',
      name: 'Admin',
      description: '',
      permissions: ['*'],
      isSystem: true,
      createdAt: null,
      updatedAt: null,
      createdBy: '',
    };
  }
}
